"use client";

import { useState } from "react";
import type { CategoryStructure } from "@/lib/categoryStructure";
import type { CategorySelection } from "@/lib/types";
import {
  resolveSofaCategorySelection,
  shouldNudgeChairTowardSofa,
} from "@/lib/listing/chairSofaCategoryNudge";

export interface ChairSofaCategoryNudgeProps {
  title: string;
  subcategoryId: string | null | undefined;
  categoryStructure: CategoryStructure[];
  onSwitchToSofa: (selection: CategorySelection) => void;
  className?: string;
}

/**
 * Soft “Did you mean Sofa?” banner when Chair is selected but the title looks like a sofa/couch.
 * Never blocks submit — Keep Chair dismisses until the sofa signal clears and returns.
 */
export default function ChairSofaCategoryNudge({
  title,
  subcategoryId,
  categoryStructure,
  onSwitchToSofa,
  className = "",
}: ChairSofaCategoryNudgeProps) {
  const signalActive = shouldNudgeChairTowardSofa(title, subcategoryId);
  const [dismissedWhileActive, setDismissedWhileActive] = useState(false);
  const [prevSignalActive, setPrevSignalActive] = useState(signalActive);

  // Reset dismiss when the sofa signal clears so a later re-match can show again.
  if (signalActive !== prevSignalActive) {
    setPrevSignalActive(signalActive);
    if (!signalActive && dismissedWhileActive) {
      setDismissedWhileActive(false);
    }
  }

  if (!signalActive || dismissedWhileActive) return null;

  const sofaSelection = resolveSofaCategorySelection(categoryStructure);
  if (!sofaSelection) return null;

  return (
    <div
      className={`mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 ${className}`}
      role="status"
    >
      <p className="text-sm text-amber-950">
        Title looks like a sofa or couch. Did you mean{" "}
        <span className="font-semibold">Sofa</span>?
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-md bg-amber-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800"
          onClick={() => onSwitchToSofa(sofaSelection)}
        >
          Switch to Sofa
        </button>
        <button
          type="button"
          className="rounded-md border border-amber-400 bg-white px-3 py-1.5 text-sm font-medium text-amber-950 hover:bg-amber-100"
          onClick={() => setDismissedWhileActive(true)}
        >
          Keep Chair
        </button>
      </div>
    </div>
  );
}
